<template>
  <div class="self-order-settings">
    <v-card>
      <v-card-title>
        <v-icon left>
          mdi-cellphone
        </v-icon>
        セルフオーダー設定
      </v-card-title>

      <v-card-text>
        <v-row>
          <!-- 左側: 設定パネル（QRコードタブ時は全幅） -->
          <v-col
            cols="12"
            :md="isFullWidthTab ? 12 : 6"
          >
            <v-tabs v-model="activeTab">
              <v-tab>テーマカラー</v-tab>
              <v-tab>機能設定</v-tab>
              <v-tab>画像設定</v-tab>
              <v-tab>カテゴリ設定</v-tab>
              <v-tab>商品画像</v-tab>
              <v-tab>QRコード</v-tab>
              <v-tab>端末管理</v-tab>
            </v-tabs>

            <v-tabs-items v-model="activeTab">
              <!-- テーマカラー設定 -->
              <v-tab-item>
                <v-card flat>
                  <v-card-text>
                    <v-row>
                      <v-col
                        v-for="colorField in colorFields"
                        :key="colorField.field"
                        cols="12"
                        sm="6"
                      >
                        <v-card outlined>
                          <v-card-subtitle>{{ colorField.label }}</v-card-subtitle>
                          <v-card-text>
                            <v-color-picker
                              :value="theme[colorField.field]"
                              mode="hexa"
                              hide-mode-switch
                              show-swatches
                              :swatches="swatches"
                              @input="updateColor(colorField.field, $event)"
                            />
                          </v-card-text>
                        </v-card>
                      </v-col>
                    </v-row>
                  </v-card-text>
                </v-card>
              </v-tab-item>

              <!-- 機能設定（指名・コースの有効化） -->
              <v-tab-item>
                <v-card flat>
                  <v-card-text>
                    <p class="text-body-2 text--secondary mb-4">
                      セルフオーダー画面で指名・コースの注文タブを表示するかを店舗ごとに設定します。
                    </p>
                    <v-switch
                      :input-value="featureFlags.nominationEnabled"
                      label="指名を有効にする"
                      inset
                      hide-details
                      class="mt-2"
                      @change="updateFeatureFlag('nominationEnabled', $event)"
                    />
                    <v-switch
                      :input-value="featureFlags.courseEnabled"
                      label="コースを有効にする"
                      inset
                      hide-details
                      class="mt-4"
                      @change="updateFeatureFlag('courseEnabled', $event)"
                    />

                    <v-divider class="my-6" />

                    <div class="text-subtitle-2 mb-1">
                      価格の注記
                    </div>
                    <p class="text-body-2 text--secondary mb-3">
                      メニュー画面のカートボタン下に表示する価格の注記です。空欄のときは既定の文言を表示します。
                    </p>
                    <v-text-field
                      :value="taxNote"
                      :placeholder="DEFAULT_TAX_NOTE"
                      :counter="TAX_NOTE_MAX_LENGTH"
                      :maxlength="TAX_NOTE_MAX_LENGTH"
                      outlined
                      dense
                      clearable
                      @input="updateTaxNote"
                    />

                    <v-divider class="my-6" />

                    <div class="text-subtitle-2 mb-1">
                      オーダーストップ
                    </div>
                    <p class="text-body-2 text--secondary mb-3">
                      モバイルオーダーからの注文受付を止めます。ボタンで今すぐ停止・再開できます。
                      自動停止時刻を設定すると、止め忘れていても毎営業日その時刻で止まります。現在の状態はページを再読み込みすると最新になります。
                    </p>
                    <self-order-stop-button
                      ref="selfOrderStopButton"
                      class="mb-4"
                    />
                    <v-text-field
                      :value="orderStopAt"
                      type="time"
                      label="自動停止時刻(任意)"
                      hint="開店時刻より前の時刻(例: 01:00)は翌日の深夜として扱います。空欄で自動停止なし。保存ボタンで反映されます"
                      persistent-hint
                      outlined
                      dense
                      clearable
                      @input="updateOrderStopAt"
                    />

                    <v-divider class="my-6" />

                    <div class="text-subtitle-2 mb-1">
                      スタッフ呼び出しタグ
                    </div>
                    <p class="text-body-2 text--secondary mb-3">
                      このタグが付いた商品は、注文時に販売履歴を作成せず、キッチンプリンタへの印刷（スタッフへの通知）のみ行います。
                    </p>
                    <v-select
                      :value="staffCallVendibleTagId"
                      :items="allTags"
                      item-text="name"
                      item-value="id"
                      label="タグを選択（未選択で機能OFF）"
                      clearable
                      outlined
                      dense
                      hide-details
                      @change="updateStaffCallTag"
                    />

                    <!-- 印刷先の選択（レシートSDP有効店舗のみ表示。無効店舗はキッチン固定） -->
                    <v-radio-group
                      v-if="receiptSdpEnabled"
                      :value="staffCallPrintTarget"
                      class="mt-4"
                      hide-details
                      @change="updateStaffCallPrintTarget"
                    >
                      <template #label>
                        <span class="text-subtitle-2">印刷先</span>
                      </template>
                      <v-radio
                        label="キッチンプリンタ（タグの振り分け設定に従う）"
                        value="kitchen"
                      />
                      <v-radio
                        label="レシートプリンタ"
                        value="receipt"
                      />
                    </v-radio-group>

                    <v-alert
                      type="info"
                      text
                      dense
                      class="mt-6 mb-0"
                    >
                      <div class="text-subtitle-2 mb-1">
                        適用条件
                      </div>
                      <div class="text-body-2">
                        上記を有効にしても、セルフオーダーの注文タブは次の<strong>いずれか</strong>を満たす場合のみ表示されます。
                      </div>
                      <ul class="text-body-2 mt-2 mb-1">
                        <li>スタッフがログインしている</li>
                        <li>登録端末（卓上タブレット）で利用している</li>
                      </ul>
                      <div class="text-caption">
                        ※ ゲストモードかつ未登録端末では表示されません。
                      </div>
                    </v-alert>
                  </v-card-text>
                </v-card>
              </v-tab-item>

              <!-- 画像設定 -->
              <v-tab-item>
                <v-card flat>
                  <v-card-text>
                    <image-uploader
                      v-model="logoFile"
                      label="店舗ロゴ"
                      hint="推奨: 200×200px（正方形）、上限1MB"
                      :preview-url="logoUrl"
                      @remove="removeImage('logo')"
                    />

                    <v-divider class="my-4" />

                    <image-uploader
                      v-model="bannerFile"
                      label="バナー画像"
                      hint="推奨: 800×400px（横長 2:1）、上限1MB"
                      :preview-url="bannerUrl"
                      @remove="removeImage('banner')"
                    />
                  </v-card-text>
                </v-card>
              </v-tab-item>

              <!-- カテゴリ設定 -->
              <v-tab-item>
                <v-card flat>
                  <v-card-text>
                    <category-settings
                      :categories="sortedCategories"
                      :available-tags="unselectedTags"
                      :icon-options="iconOptions"
                      @add="addCategory"
                      @remove="removeCategory"
                      @update-icon="updateCategoryIcon"
                      @reorder="reorderCategories"
                    />
                  </v-card-text>
                </v-card>
              </v-tab-item>

              <!-- 商品画像設定 -->
              <v-tab-item>
                <v-card flat>
                  <v-card-text>
                    <product-image-settings :categories="sortedCategories" />
                  </v-card-text>
                </v-card>
              </v-tab-item>

              <!-- QRコード一覧 -->
              <v-tab-item>
                <v-card flat>
                  <v-card-text>
                    <qr-code-list />
                  </v-card-text>
                </v-card>
              </v-tab-item>

              <!-- タブレット端末管理 (.steering/20260420-mobile-order-tablet-kiosk-mode/) -->
              <v-tab-item>
                <v-card flat>
                  <v-card-text>
                    <mobile-order-device-list />
                  </v-card-text>
                </v-card>
              </v-tab-item>
            </v-tabs-items>

            <v-card-actions v-if="activeTab < 3">
              <v-spacer />
              <v-btn
                color="primary"
                :loading="saving"
                :disabled="loading"
                @click="saveSettings"
              >
                <v-icon left>
                  {{ icons.mdiContentSave }}
                </v-icon>
                保存
              </v-btn>
            </v-card-actions>
          </v-col>

          <!-- 右側: プレビュー（QRコード・端末管理タブ時は非表示） -->
          <v-col
            v-if="!isFullWidthTab"
            cols="12"
            md="6"
          >
            <div class="preview-section">
              <div class="d-flex align-center mb-4">
                <span class="text-subtitle-1 font-weight-medium">プレビュー</span>
                <v-spacer />
                <v-btn-toggle
                  v-model="deviceSize"
                  mandatory
                  dense
                >
                  <v-btn
                    value="mobile"
                    small
                  >
                    モバイル
                  </v-btn>
                  <v-btn
                    value="tablet"
                    small
                  >
                    タブレット
                  </v-btn>
                </v-btn-toggle>
              </div>

              <mobile-order-preview
                :device-size="deviceSize"
                :theme="theme"
                :logo-url="logoPreviewUrl"
                :banner-url="bannerPreviewUrl"
                :categories="sortedCategories"
                :icon-options="iconOptions"
              />
            </div>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- 成功/エラーメッセージ -->
    <v-snackbar
      v-model="showSnackbar"
      :color="snackbarColor"
      timeout="3000"
    >
      {{ snackbarMessage }}
    </v-snackbar>
  </div>
</template>

<script>
import {
  ref, computed, onMounted, watch,
} from '@vue/composition-api'
import { mdiContentSave } from '@mdi/js'
import { useStore } from '@/views/composable/useStore'
import { DEFAULT_TAX_NOTE, TAX_NOTE_MAX_LENGTH } from '@/store/admin/selfOrderSettings'
import ImageUploader from '@/components/admin/ImageUploader.vue'
import CategorySettings from '@/components/admin/CategorySettings.vue'
import MobileOrderPreview from '@/components/admin/MobileOrderPreview.vue'
import ProductImageSettings from '@/components/admin/ProductImageSettings.vue'
import QrCodeList from '@/components/admin/QrCodeList.vue'
import MobileOrderDeviceList from '@/components/admin/MobileOrderDeviceList.vue'
import SelfOrderStopButton from '@/views/components/self_order/SelfOrderStopButton.vue'

export default {
  name: 'SelfOrderSettings',

  components: {
    ImageUploader,
    CategorySettings,
    MobileOrderPreview,
    ProductImageSettings,
    SelfOrderStopButton,
    QrCodeList,
    MobileOrderDeviceList,
  },

  setup() {
    const store = useStore()
    const activeTab = ref(0)
    const deviceSize = ref('mobile')

    // プレビューを非表示にするタブ（QRコード、端末管理）
    const isFullWidthTab = computed(() => activeTab.value === 5 || activeTab.value === 6)

    // 画像ファイル
    const logoFile = ref(null)
    const bannerFile = ref(null)

    // 画像削除フラグ（ユーザーが×を押したら立ち、次の保存時にサーバー側も削除）
    const removeLogoFlag = ref(false)
    const removeBannerFlag = ref(false)

    // スナックバー
    const showSnackbar = ref(false)
    const snackbarMessage = ref('')
    const snackbarColor = ref('success')

    // カラーフィールド定義
    const colorFields = [
      { field: 'primaryColor', label: 'プライマリカラー（アクセント）' },
      { field: 'backgroundColor', label: '背景カラー' },
      { field: 'cardColor', label: 'カードカラー' },
      { field: 'textColor', label: 'テキストカラー' },
    ]

    // カラースウォッチ
    const swatches = [
      ['#d4bc83', '#c9a961', '#b8993f'],
      ['#1a1826', '#232030', '#2d2a3e'],
      ['#ffffff', '#f5f5f5', '#e0e0e0'],
      ['#f44336', '#e91e63', '#9c27b0'],
    ]

    // Vuexゲッター
    const loading = computed(() => store.getters['admin/selfOrderSettings/loading'])
    const saving = computed(() => store.getters['admin/selfOrderSettings/saving'])
    const theme = computed(() => store.getters['admin/selfOrderSettings/theme'])
    const featureFlags = computed(() => store.getters['admin/selfOrderSettings/featureFlags'])
    const logoUrl = computed(() => store.getters['admin/selfOrderSettings/logoUrl'])
    const bannerUrl = computed(() => store.getters['admin/selfOrderSettings/bannerUrl'])
    const sortedCategories = computed(() => store.getters['admin/selfOrderSettings/sortedCategories'])
    const unselectedTags = computed(() => store.getters['admin/selfOrderSettings/unselectedTags'])
    const iconOptions = computed(() => store.getters['admin/selfOrderSettings/iconOptions'])
    const allTags = computed(() => store.getters['admin/selfOrderSettings/allTags'])
    const staffCallVendibleTagId = computed(() => store.getters['admin/selfOrderSettings/staffCallVendibleTagId'])
    const staffCallPrintTarget = computed(() => store.getters['admin/selfOrderSettings/staffCallPrintTarget'])
    const receiptSdpEnabled = computed(() => store.getters['admin/selfOrderSettings/receiptSdpEnabled'])
    const taxNote = computed(() => store.getters['admin/selfOrderSettings/taxNote'])
    const orderStopAt = computed(() => store.getters['admin/selfOrderSettings/orderStopAt'])
    const selfOrderStopButton = ref(null)

    // プレビュー用URL（ファイル選択時はローカルURL）
    const logoPreviewUrl = computed(() => {
      if (logoFile.value) {
        return URL.createObjectURL(logoFile.value)
      }

      return logoUrl.value
    })

    const bannerPreviewUrl = computed(() => {
      if (bannerFile.value) {
        return URL.createObjectURL(bannerFile.value)
      }

      return bannerUrl.value
    })

    // メソッド
    const updateColor = (field, value) => {
      // カラーピッカーがオブジェクトを返す場合があるので対応
      const colorValue = typeof value === 'string' ? value : value.hex
      store.dispatch('admin/selfOrderSettings/updateThemeColor', { field, value: colorValue })
    }

    const updateFeatureFlag = (field, value) => {
      store.dispatch('admin/selfOrderSettings/updateFeatureFlag', { field, value: !!value })
    }

    const updateStaffCallTag = value => {
      // クリア時はnull（保存時に''へ変換されバックエンドでnilになる）
      store.dispatch('admin/selfOrderSettings/updateFeatureFlag', { field: 'staffCallVendibleTagId', value: value ?? null })
    }

    const updateStaffCallPrintTarget = value => {
      store.dispatch('admin/selfOrderSettings/updateFeatureFlag', { field: 'staffCallPrintTarget', value })
    }

    const updateOrderStopAt = value => {
      // clearable のクリアは null が来る。保存時に '' へ変換されバックエンドで nil(自動停止なし)になる
      store.dispatch('admin/selfOrderSettings/updateFeatureFlag', { field: 'orderStopAt', value: value ?? '' })
    }

    const updateTaxNote = value => {
      // clearable のクリアは null が来る。保存時に '' へ変換されバックエンドで nil(既定文言)になる
      store.dispatch('admin/selfOrderSettings/updateFeatureFlag', { field: 'taxNote', value: value ?? '' })
    }

    const addCategory = vendibleTagId => {
      store.dispatch('admin/selfOrderSettings/addCategory', vendibleTagId)
    }

    const removeCategory = vendibleTagId => {
      store.dispatch('admin/selfOrderSettings/removeCategory', vendibleTagId)
    }

    const updateCategoryIcon = ({ vendibleTagId, icon }) => {
      store.dispatch('admin/selfOrderSettings/updateCategoryIcon', { vendibleTagId, icon })
    }

    const reorderCategories = categories => {
      store.dispatch('admin/selfOrderSettings/reorderCategories', categories)
    }

    const removeImage = async type => {
      // 画像削除は即時サーバーへ反映する（ボタンのみで完結）
      switch (type) {
        case 'logo':
          logoFile.value = null
          removeLogoFlag.value = true
          break
        case 'banner':
          bannerFile.value = null
          removeBannerFlag.value = true
          break
        default:
          return
      }

      try {
        await store.dispatch('admin/selfOrderSettings/saveSetting', {
          logo: null,
          banner: null,
          removeLogo: removeLogoFlag.value,
          removeBanner: removeBannerFlag.value,
        })

        removeLogoFlag.value = false
        removeBannerFlag.value = false

        snackbarMessage.value = '画像を削除しました'
        snackbarColor.value = 'success'
        showSnackbar.value = true
      } catch (error) {
        snackbarMessage.value = '画像の削除に失敗しました'
        snackbarColor.value = 'error'
        showSnackbar.value = true
      }
    }

    const saveSettings = async () => {
      try {
        // テーマ設定を保存（カテゴリは各操作時に即時保存済み）
        await store.dispatch('admin/selfOrderSettings/saveSetting', {
          logo: logoFile.value,
          banner: bannerFile.value,
          removeLogo: removeLogoFlag.value,
          removeBanner: removeBannerFlag.value,
        })

        // ファイル入力と削除フラグをクリア
        logoFile.value = null
        bannerFile.value = null
        removeLogoFlag.value = false
        removeBannerFlag.value = false

        // 自動停止時刻の変更をオーダーストップの状態表示に反映
        selfOrderStopButton.value?.refresh?.()

        snackbarMessage.value = '設定を保存しました'
        snackbarColor.value = 'success'
        showSnackbar.value = true
      } catch (error) {
        snackbarMessage.value = '保存に失敗しました'
        snackbarColor.value = 'error'
        showSnackbar.value = true
      }
    }

    // 初期化
    onMounted(async () => {
      try {
        await Promise.all([
          store.dispatch('admin/selfOrderSettings/fetchSetting'),
          store.dispatch('admin/selfOrderSettings/fetchVendibleTags'),
        ])
      } catch (error) {
        snackbarMessage.value = '設定の読み込みに失敗しました'
        snackbarColor.value = 'error'
        showSnackbar.value = true
      }
    })

    return {
      // data
      activeTab,
      isFullWidthTab,
      deviceSize,
      logoFile,
      bannerFile,
      showSnackbar,
      snackbarMessage,
      snackbarColor,
      colorFields,
      swatches,

      // computed
      loading,
      saving,
      theme,
      featureFlags,
      logoUrl,
      bannerUrl,
      logoPreviewUrl,
      bannerPreviewUrl,
      sortedCategories,
      unselectedTags,
      iconOptions,
      allTags,
      staffCallVendibleTagId,
      staffCallPrintTarget,
      receiptSdpEnabled,
      taxNote,
      orderStopAt,
      selfOrderStopButton,
      DEFAULT_TAX_NOTE,
      TAX_NOTE_MAX_LENGTH,

      // methods
      updateColor,
      updateFeatureFlag,
      updateStaffCallTag,
      updateStaffCallPrintTarget,
      updateTaxNote,
      updateOrderStopAt,
      addCategory,
      removeCategory,
      updateCategoryIcon,
      reorderCategories,
      removeImage,
      saveSettings,

      // icons
      icons: {
        mdiContentSave,
      },
    }
  },
}
</script>

<style scoped>
.self-order-settings {
  padding: 16px;
}

.preview-section {
  position: sticky;
  top: 80px;
}
</style>
